/* eslint-disable @typescript-eslint/no-extra-non-null-assertion */
import { computed, Injectable, signal } from '@angular/core';
import { HttpClientService } from '@core/services/http-client/http-client.service';
import { API_URL_CONSTANT } from '@sharedModule/constants/api-url.constant';
import { Observable } from 'rxjs';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { LoaderService } from '@sharedModule/services/loader/loader.service';
import { BASIC_RESPONSE_INTERFACE } from '../types/basicPrompt-interface';
import { DOCX_RESPONSE_INTERFACE } from '../types/withDocx-response-interface';
import {
  FIRST_API_CALL,
  FIVE_API_CALL,
  SECOND_API_CALL,
  THREE_API_CALL,
} from './constant/form.constant';
import { FILE_UPLOAD_INTERFACE } from '../types/fileUpload-interface';
import {
  DEFAULT_PROMPT_RES_DATA,
  PROMPT_RES_DATA_INTERFACE,
} from '../types/promptResData-interface';
import { FIRST_API_CALL_INTERFACE } from '../types/firstApi-interface';
import { THIRD_API_INTERFACE } from '../types/thirdAPI-interface';
@Injectable({
  providedIn: 'root',
})
export class SecondPromptService {
  promptResponseData = signal<PROMPT_RES_DATA_INTERFACE>(
    DEFAULT_PROMPT_RES_DATA
  );
  finalResponse_Signal = computed(() => {
    const introduction = this.splitSection(this.prompt1_Ouptut_Signal());
    const discussion = this.splitSection(this.prompt3_Ouptut_Signal());

    return {
      prompt1: introduction.body,
      prompt1References: introduction.references,
      // Results are supplied without a reference list, so any trailing
      // bibliography the results step emitted is dropped here.
      prompt2: this.splitSection(this.prompt2_Ouptut_Signal()).body,
      prompt3: discussion.body,
      prompt3References: discussion.references,
    };
  });
  userSignal = signal<string>('publication');
  s3UploadResponse_Signal = signal<FILE_UPLOAD_INTERFACE | undefined>(
    undefined
  );

  prompt1_Ouptut_Signal = signal<string>('');
  prompt2_Ouptut_Signal = signal<string>('');
  prompt3_Ouptut_Signal = signal<string>('');
  prompt5_Ouptut_Signal = signal<string>('');
  prompt5_Payload_Signal = signal<string>('');

  constructor(
    public httpClientService: HttpClientService,
    public messageHandlerService: MessageHandlerService,
    public loaderService: LoaderService
  ) {}

  firstPrompt(
    data: FIRST_API_CALL_INTERFACE
  ): Observable<BASIC_RESPONSE_INTERFACE> {
    const manipulatedData2 = { ...data };
    const payloadWithOutline =
      FIRST_API_CALL.staticText1 +
      `The outline to follow is : ${manipulatedData2?.introductiveOutline}  \n\n` +
      `The objective of this study is : ${manipulatedData2?.objective} \n\n` +
      FIRST_API_CALL.staticArticleText +
      ` ${manipulatedData2?.articles}`;

    const payload = {
      prompt: payloadWithOutline,
    };

    return this.httpClientService.post(API_URL_CONSTANT.prompt1, payload);
  }

  secondPromptwithDocx(form: FormData): Observable<DOCX_RESPONSE_INTERFACE> {
    const manipulatedData = Object.values(form);

    const data = {
      prompt: SECOND_API_CALL.staticText1 + ':' + manipulatedData[1],
      fileName: `${this.s3UploadResponse_Signal()?.fileName}`,
      format: `${this.s3UploadResponse_Signal()?.type}`,
    };

    return this.httpClientService.post(API_URL_CONSTANT.prompt2, data);
  }

  thirdPrompt(data: THIRD_API_INTERFACE): Observable<BASIC_RESPONSE_INTERFACE> {
    const manipulatedData = { ...data };

    const payloadWithOutline =
      `${THREE_API_CALL.staticText1}
      ${manipulatedData?.findings}` +
      `${THREE_API_CALL.staticText2}: ${manipulatedData?.discussionArticles}` +
      `Overall, follow this outline provided for the discussion section:  ${manipulatedData?.discussionOutline}` +
      THREE_API_CALL.staticText3;
    const payload = {
      prompt: payloadWithOutline,
    };
    return this.httpClientService.post(API_URL_CONSTANT.prompt3, payload);
  }

  /**
   * Splits a generated section into its body and its trailing bibliography.
   *
   * The heading match tolerates a Markdown prefix: Haiku 4.5 emits `# References`
   * despite the relay asking for plain text, and without the `#{0,6}` the split
   * silently failed — leaving the reference list glued to the body and sending an
   * empty references block into the combine step. The backend's
   * `splitOnReferenceHeading` already allows for this.
   */
  splitSection(section: string): { body: string; references: string } {
    const heading =
      /^[ \t]*#{0,6}[ \t]*(References?|Bibliography)[ \t]*:?[ \t]*$/im.exec(
        section
      );

    if (!heading) {
      return { body: section.trim(), references: '' };
    }

    return {
      body: section.slice(0, heading.index).trim(),
      references: section.slice(heading.index + heading[0].length).trim(),
    };
  }

  fifthPrompt(): Observable<BASIC_RESPONSE_INTERFACE> {
    const response = this.finalResponse_Signal();

    const replacement =
      `\n\n${FIVE_API_CALL.introLabel}\n${response.prompt1}` +
      `\n\n${FIVE_API_CALL.introReferenceLabel}\n${response.prompt1References}` +
      `\n\n${FIVE_API_CALL.resultLabel}\n${response.prompt2}` +
      `\n\n${FIVE_API_CALL.discussionLabel}\n${response.prompt3}` +
      `\n\n${FIVE_API_CALL.discussionReferenceLabel}\n${response.prompt3References}`;

    this.prompt5_Payload_Signal.set(replacement);

    const payload = {
      prompt:
        `${FIVE_API_CALL.staticText1}` +
        `\n${replacement}` +
        `\n\n${FIVE_API_CALL.staticText2}` +
        `\n\n${FIVE_API_CALL.staticText3}` +
        `\n\n${FIVE_API_CALL.staticText4}`,
    };

    return this.httpClientService.post(API_URL_CONSTANT.prompt5, payload);
  }
}
